<script setup lang="ts">
import {pluralize} from "@/core/filters/Pluralize";
import BaseButton from "@/core/components/base/BaseButton.vue";
import {Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger} from "@/core/components/ui/dialog";
import SearchInput from "@/core/components/search-bar/SearchInput.vue";
import {TableCell, TableRow} from "@/core/components/ui/table";
import {ref} from "vue";
import {
    AUTO_COMPLETE_REQUEST_TYPE,
    SearchBarAutoCompleteApiResponse, SearchBarAutoCompleteItem
} from "@/auto-complete-searchbar/interfaces/ISearchBarAutoComplete";
import debounce from "lodash.debounce";
import {searchBarApi} from "@/container";

const props = defineProps<{ selectedProducts: SearchBarAutoCompleteItem[] }>();
defineEmits(['on-select']);
const open = ref(false);
const loading = ref<boolean>(false);
const results = ref<SearchBarAutoCompleteApiResponse | null>(null);

/**
 * Search function.
 *
 * @param {string} value
 *
 * @return {Promise<SearchBarAutoCompleteApiResponse>}
 */
const search = debounce(async (value: string): Promise<void> => {
    if (!value || value.length <= 3) {
        clear();

        return;
    }

    loading.value = true;
    results.value = await searchBarApi().autocomplete(
        value,
        [
            AUTO_COMPLETE_REQUEST_TYPE.PRODUCT,
            AUTO_COMPLETE_REQUEST_TYPE.LABORATORY,
            AUTO_COMPLETE_REQUEST_TYPE.RANGE
        ],
        5
    ).catch(() => null);
    loading.value = false;
}, 300);

/**
 * Clear the search results.
 */
const clear = () => {
    results.value = null;
};

/**
 * Determine if an item is already selected.
 * @param item
 */
const isSelected = (item: SearchBarAutoCompleteItem): boolean => {
    return props.selectedProducts.find(product => product.id === item.id && product.type === item.type) !== undefined;
};
</script>

<template>
  <Dialog v-model:open="open">
    <DialogTrigger as-child>
      <BaseButton>Sélectionner les produits</BaseButton>
    </DialogTrigger>
    <DialogContent>
      <DialogHeader>
        <DialogTitle>Sélectionner les produits</DialogTitle>
      </DialogHeader>
      <DialogDescription />
      <div>
        <SearchInput
          :is-loading="loading"
          class="bg-gray-50"
          placeholder="Produit, laboratoire ou gamme ..."
          @input="search"
        />
      </div>
      <div class="min-h-72 max-h-96 overflow-y-auto">
        <div v-if="results" class="flex flex-col gap-y-3 py-3">
          <div class="flex flex-col gap-y-3">
            <h3>Produits</h3>
            <Table>
              <TableRow
                v-for="product in results.product"
                :key="'product' + product.id"
                class="cursor-pointer"
                :class="[{'bg-primary': isSelected(product)}]"
                @click="$emit('on-select',product)"
              >
                <TableCell>{{ product.id }}</TableCell>
                <TableCell>{{ product.cip }}</TableCell>
                <TableCell>{{ product.name }}</TableCell>
              </TableRow>
            </Table>
          </div>
          <div class="flex flex-col gap-y-3">
            <h3>Laboratoires</h3>
            <Table>
              <TableRow
                v-for="laboratory in results.laboratory"
                :key="'lab' + laboratory.id"
                class="cursor-pointer"
                :class="[{'bg-primary': isSelected(laboratory)}]"
                @click="$emit('on-select',laboratory)"
              >
                <TableCell>{{ laboratory.id }}</TableCell>
                <TableCell>{{ laboratory.name }}</TableCell>
              </TableRow>
            </Table>
          </div>
          <div class="flex flex-col gap-y-3">
            <h3>Gammes</h3>
            <Table>
              <TableRow
                v-for="range in results.ranges"
                :key="'range' + range.id"
                class="cursor-pointer"
                :class="[{'bg-primary': isSelected(range)}]"
                @click="$emit('on-select', range)"
              >
                <TableCell>{{ range.id }}</TableCell>
                <TableCell>{{ range.name }}</TableCell>
              </TableRow>
            </Table>
          </div>
        </div>
      </div>
      <div v-if="selectedProducts.length" class="flex justify-center">
        <BaseButton primary @click="open = false">
          Confirmer {{ pluralize('le', selectedProducts.length) }}
          {{ selectedProducts.length === 1 ? '' : selectedProducts.length }}
          {{ pluralize('produit', selectedProducts.length) }}
        </BaseButton>
      </div>
    </DialogContent>
  </Dialog>
</template>
